import { useState } from "react";
import { AlertTriangle, PackageSearch, SlidersHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { useFavoriteToggleHandler } from "@/features/engagement/hooks/useFavoriteToggleHandler";
import { useWishlist } from "@/features/engagement/hooks/useWishlist";
import { FilterSidebar } from "@/features/marketplace/components/FilterSidebar";
import { ProductCard, ProductCardSkeleton } from "@/features/marketplace/components/ProductCard";
import { SortDropdown } from "@/features/marketplace/components/SortDropdown";
import { useSearchFilters } from "@/features/marketplace/hooks/useSearchFilters";
import { useSearchProducts } from "@/features/marketplace/hooks/useSearchProducts";
import { ApiError } from "@/types/api";

const SKELETON_COUNT = 9;

/**
 * docs/ui/ui-architecture.md screen #5 — Search Results. Desktop renders
 * `FilterSidebar` as a persistent column (docs/ui/design-system.md §11);
 * mobile renders the identical component inside the shared `Drawer` — same
 * filter model, different container, per that section's own rule.
 */
export function SearchResultsPage() {
  const { filters, updateFilters, clearFilters } = useSearchFilters();
  const searchQuery = useSearchProducts(filters);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const wishlistQuery = useWishlist();
  const favoriteIds = new Set(wishlistQuery.data?.map((product) => product.id) ?? []);
  const handleFavoriteToggle = useFavoriteToggleHandler(favoriteIds);

  const totalPages = searchQuery.data?.meta.totalPages ?? 1;

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={filters.q}
          onChange={(value) => updateFilters({ q: value })}
          placeholder="Search BazaarX"
          className="flex-1"
        />
        <div className="flex items-center gap-2">
          <Drawer open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
            <DrawerTrigger asChild>
              <Button type="button" variant="outline" className="lg:hidden">
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                Filters
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              {/* Visually hidden — FilterSidebar renders its own visible
                  "Filters" heading right below; Radix still requires an
                  accessible dialog title, it just doesn't need to be seen
                  twice. */}
              <DrawerTitle className="sr-only">Filters</DrawerTitle>
              <div className="px-4 pb-6 pt-6">
                <FilterSidebar filters={filters} onChange={updateFilters} onClear={clearFilters} />
              </div>
            </DrawerContent>
          </Drawer>
          <SortDropdown value={filters.sort} onChange={(sort) => updateFilters({ sort })} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block">
          <FilterSidebar filters={filters} onChange={updateFilters} onClear={clearFilters} />
        </aside>

        <div>
          {searchQuery.isPending && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                <ProductCardSkeleton key={index} />
              ))}
            </div>
          )}

          {searchQuery.isError && (
            <EmptyState
              icon={AlertTriangle}
              heading="Couldn't load results"
              description={
                searchQuery.error instanceof ApiError
                  ? searchQuery.error.message
                  : "Something went wrong. Please try again."
              }
              action={{ label: "Retry", onClick: () => void searchQuery.refetch() }}
            />
          )}

          {searchQuery.data?.data.length === 0 && (
            <EmptyState
              icon={PackageSearch}
              heading="No results found"
              description="We couldn't find anything matching your search."
              action={{ label: "Clear filters", onClick: clearFilters }}
            />
          )}

          {searchQuery.data && searchQuery.data.data.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {searchQuery.data.data.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isFavorite={favoriteIds.has(product.id)}
                    onFavoriteToggle={handleFavoriteToggle}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination className="mt-8">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href={filters.page > 1 ? "#" : undefined}
                        aria-disabled={filters.page <= 1}
                        className={filters.page <= 1 ? "pointer-events-none opacity-50" : undefined}
                        onClick={(event) => {
                          event.preventDefault();
                          if (filters.page > 1) updateFilters({ page: filters.page - 1 });
                        }}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="px-4 text-body-sm text-muted-foreground">
                        Page {filters.page} of {totalPages}
                      </span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href={filters.page < totalPages ? "#" : undefined}
                        aria-disabled={filters.page >= totalPages}
                        className={
                          filters.page >= totalPages ? "pointer-events-none opacity-50" : undefined
                        }
                        onClick={(event) => {
                          event.preventDefault();
                          if (filters.page < totalPages) updateFilters({ page: filters.page + 1 });
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
